import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { User as UserIcon, Lock, Building2 } from "lucide-react";

export default function MyProfile() {
  const { user, company, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) setForm({ name: user.name || "", phone: user.phone || "" }); }, [user]);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.put("/me/profile", form);
      await refresh();
      toast.success("Profile updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const changePw = async () => {
    if (pw.new_password !== pw.confirm) { toast.error("Passwords don't match"); return; }
    if (pw.new_password.length < 6) { toast.error("Password too short"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">My profile</h1>
        <p className="text-sm text-slate-500 mt-1">Your account settings and security</p>
      </div>

      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="w-16 h-16">
            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xl font-bold">
              {(user?.name || "U").slice(0,2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="text-lg font-semibold text-slate-900">{user?.name}</div>
            <div className="text-sm text-slate-500">{user?.email}</div>
            <div className="mt-1 flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 capitalize">{user?.role?.replace("_"," ")}</Badge>
              {company && <span className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3"/>{company.name}</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Full name</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="profile-name-input"/></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="profile-phone-input"/></div>
          <div><Label>Email</Label><Input value={user?.email || ""} disabled/></div>
          <div><Label>Company</Label><Input value={company?.name || ""} disabled/></div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveProfile} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-save-button">Save changes</Button>
        </div>
      </Card>

      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-4 h-4 text-slate-500"/>
          <div className="font-semibold text-slate-900">Change password</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label>Current password</Label><Input type="password" value={pw.current_password} onChange={(e)=>setPw({...pw,current_password:e.target.value})} data-testid="pw-current-input"/></div>
          <div><Label>New password</Label><Input type="password" value={pw.new_password} onChange={(e)=>setPw({...pw,new_password:e.target.value})} data-testid="pw-new-input"/></div>
          <div><Label>Confirm new</Label><Input type="password" value={pw.confirm} onChange={(e)=>setPw({...pw,confirm:e.target.value})} data-testid="pw-confirm-input"/></div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={changePw} disabled={busy || !pw.current_password || !pw.new_password} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pw-save-button">Update password</Button>
        </div>
      </Card>
    </div>
  );
}
